import { useEffect, useRef, useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAuthHttp } from '@hooks';
import { SUPPLIER_GET } from '@constants';
import { MdAddCircleOutline } from 'react-icons/md';
import {
  ChevronUpIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/20/solid';
import { Supplier } from '@views/SupplierMaster/supplierRecord';
import { ProductSkusFormValues, SkuServerError } from './types';
import { FormPage } from '../../tailwindComponents/FormPage';
import { CreateSupplierModal } from '../CreateInboundShipmentNoteForm/CreateSupplierModal';
import { ProductSection } from './ProductSection';
import { SkuCard } from './SkuCard';
import { productSkusSchema } from './validation';
import { duplicateSkuCard, emptySkuCard } from './payload';
import { ProductSkuExplainer } from './ProductSkuExplainer';
import { buildSkuChainSummary } from './skuDerivation';
import { hasNewProductId } from './clone';
import { describeServerError, mapServerErrorToFormPath } from './serverErrors';

const defaultFormValues = (): ProductSkusFormValues => ({
  productId: '',
  productName: '',
  productDescription: '',
  productBrand: '',
  productCategory: '',
  productSubCategory: '',
  productNetVolume: '',
  productNetVolumeUnitOfMeasure: { label: '', value: '' },
  productBarcode: '',
  productImage: null,
  skuProductImage: null,
  deletedProductImageId: '',
  productBarcodeImage: null,
  skuProductBarcodeImage: null,
  deletedProductBarcodeImageId: '',
  skus: [emptySkuCard()],
});

export function SKUForm({
  onSubmit,
  isSubmitting,
  header,
  pages,
  initialValues,
  lockedProduct = false,
  mode = 'create',
  cloneSourceProductId = null,
  unclonedSkuCount = 0,
  serverErrors = null,
  onDismissServerErrors,
}: {
  onSubmit: (data: ProductSkusFormValues) => void;
  isSubmitting: boolean;
  header: string;
  pages: { name: string; href: string; current: boolean }[];
  initialValues?: ProductSkusFormValues;
  lockedProduct?: boolean;
  mode?: 'create' | 'edit';
  cloneSourceProductId?: string | null;
  unclonedSkuCount?: number;
  serverErrors?: SkuServerError[] | null;
  onDismissServerErrors?: () => void;
}) {
  const authHttp = useAuthHttp();
  const isCloning = cloneSourceProductId !== null;
  const [currentStep, setCurrentStep] = useState(1);
  const [isProductCollapsed, setIsProductCollapsed] = useState(
    () => Boolean(initialValues?.productId) && !lockedProduct && !isCloning
  );
  const [collapsedCardIds, setCollapsedCardIds] = useState<Set<string>>(
    new Set()
  );
  const [supplierOptions, setSupplierOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [supplierModalCardIndex, setSupplierModalCardIndex] = useState<
    number | null
  >(null);
  const [unmappedServerErrors, setUnmappedServerErrors] = useState<string[]>(
    []
  );

  const methods = useForm<ProductSkusFormValues>({
    shouldUnregister: false,
    resolver: yupResolver(productSkusSchema),
    defaultValues: initialValues ?? defaultFormValues(),
  });

  const { formState, control, setValue, getValues, setError } = methods;

  const { fields, append, remove, insert } = useFieldArray({
    control,
    name: 'skus',
  });

  const productIdValue = useWatch({ control, name: 'productId' });
  const watchedCards = useWatch({ control, name: 'skus' });
  const skuChainSummary =
    mode === 'create' ? buildSkuChainSummary(watchedCards ?? []) : null;
  const isCloneWaitingOnANewProductId =
    isCloning && !hasNewProductId(cloneSourceProductId, productIdValue);
  const cloneWarning = cloneSourceProductId
    ? `This clone only exists on this screen. Nothing is saved until you give it a Product ID of its own — changing SKU or pallet details alone still points at ${cloneSourceProductId}, so no new product would be created.`
    : 'This clone only exists on this screen. Nothing is saved until you give it a Product ID.';

  const fetchSuppliers = async () => {
    try {
      const { data } = await authHttp.post(SUPPLIER_GET, {
        pagination: { itemsPerPage: 100 },
      });
      setSupplierOptions(
        (data.suppliers || []).map((supplier: Supplier) => ({
          label: supplier.name,
          value: supplier.id,
        }))
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  useEffect(() => {
    fetchSuppliers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const didInitialiseCollapse = useRef(false);
  useEffect(() => {
    if (
      didInitialiseCollapse.current ||
      mode === 'edit' ||
      fields.length === 0
    ) {
      return;
    }
    didInitialiseCollapse.current = true;
    if (fields.length > 1) {
      setCollapsedCardIds(new Set(fields.slice(1).map((field) => field.id)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, mode]);

  const scrollToFirstError = (
    productHasErrors: boolean,
    erroredIndices: Set<number>
  ) => {
    requestAnimationFrame(() => {
      const target = productHasErrors
        ? document.querySelector('[data-product-column]')
        : document.querySelector(
            `[data-sku-card="${Math.min(...erroredIndices)}"]`
          );
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const expandCardsAtIndices = (indices: Set<number>) => {
    if (indices.size === 0) return;
    setCollapsedCardIds((previous) => {
      const next = new Set(previous);
      fields.forEach((field, index) => {
        if (indices.has(index)) next.delete(field.id);
      });
      return next;
    });
  };

  useEffect(() => {
    if (formState.submitCount === 0) return;
    const erroredIndices = new Set<number>();
    fields.forEach((_, index) => {
      if (formState.errors.skus?.[index]) erroredIndices.add(index);
    });
    expandCardsAtIndices(erroredIndices);
    const productHasErrors = Boolean(
      formState.errors.productId ||
        formState.errors.productNetVolume ||
        formState.errors.productNetVolumeUnitOfMeasure
    );
    if (productHasErrors) {
      setIsProductCollapsed(false);
    }
    if (productHasErrors || erroredIndices.size > 0) {
      scrollToFirstError(productHasErrors, erroredIndices);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.submitCount]);

  useEffect(() => {
    if (!serverErrors?.length) {
      setUnmappedServerErrors([]);
      return;
    }
    const unmapped: string[] = [];
    const erroredIndices = new Set<number>();
    serverErrors.forEach((serverError) => {
      if (
        typeof serverError.skuIndex === 'number' &&
        serverError.skuIndex >= 0
      ) {
        erroredIndices.add(serverError.skuIndex);
      }
      const path = mapServerErrorToFormPath(serverError);
      if (path) {
        setError(path as never, {
          type: 'server',
          message: serverError.message,
        });
        if (!path.startsWith('skus.')) {
          setIsProductCollapsed(false);
        }
      } else {
        unmapped.push(describeServerError(serverError));
      }
    });
    setUnmappedServerErrors(unmapped);
    expandCardsAtIndices(erroredIndices);
    const productHasServerErrors = serverErrors.some((serverError) => {
      const path = mapServerErrorToFormPath(serverError);
      return Boolean(path && !path.startsWith('skus.'));
    });
    if (productHasServerErrors || erroredIndices.size > 0) {
      scrollToFirstError(productHasServerErrors, erroredIndices);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverErrors]);

  const toggleCardCollapsed = (id: string) => {
    setCollapsedCardIds((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const collapseAllCurrentCards = () =>
    setCollapsedCardIds(new Set(fields.map((field) => field.id)));

  const handleAddSku = () => {
    const productId = getValues('productId');
    collapseAllCurrentCards();
    append(
      emptySkuCard({
        skuIdPrefix: productId ? `${productId}-` : '',
        asAdditional: true,
      })
    );
  };

  const handleDuplicateSku = (index: number) => {
    collapseAllCurrentCards();
    insert(index + 1, duplicateSkuCard(getValues(`skus.${index}`)));
  };

  const handleSupplierCreated = (newSupplier: Supplier) => {
    const newOption = { label: newSupplier.name, value: newSupplier.id };
    setSupplierOptions((previous) => [...previous, newOption]);
    if (supplierModalCardIndex !== null) {
      const currentSuppliers =
        getValues(`skus.${supplierModalCardIndex}.suppliers`) ?? [];
      setValue(`skus.${supplierModalCardIndex}.suppliers`, [
        ...currentSuppliers,
        newOption,
      ]);
    }
  };

  const resolveSubmitText = () => {
    if (mode === 'edit') return 'Save changes';
    const skuCount = fields.length;
    const skuNoun = skuCount === 1 ? 'SKU' : `${skuCount} SKUs`;
    return lockedProduct ? `Add ${skuNoun}` : `Create product & ${skuNoun}`;
  };

  const skusArrayError = (
    formState.errors.skus as { message?: string } | undefined
  )?.message;

  return (
    <FormPage<ProductSkusFormValues>
      header={header}
      pages={pages}
      methods={methods}
      currentStep={currentStep}
      setCurrentStep={setCurrentStep}
      onSubmit={onSubmit}
      isSubmitting={isSubmitting}
      cancelUrl='/sku-master'
      submitText={resolveSubmitText()}
      canSubmit={!isCloneWaitingOnANewProductId}
      submitError={
        serverErrors?.length
          ? 'Nothing was saved — fix the highlighted issues and resubmit.'
          : null
      }
      onDismissError={onDismissServerErrors}
    >
      <div className='mx-auto w-full max-w-7xl space-y-4'>
        {isCloneWaitingOnANewProductId && (
          <div className='flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 font-inter text-sm text-amber-900'>
            <ExclamationTriangleIcon className='mt-0.5 h-5 w-5 shrink-0 text-amber-500' />
            <span>{cloneWarning}</span>
          </div>
        )}
        {isCloning && unclonedSkuCount > 0 && (
          <div className='rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 font-inter text-sm text-gray-600'>
            {unclonedSkuCount} further SKU
            {unclonedSkuCount === 1 ? '' : 's'} of this product could not be
            loaded, so {unclonedSkuCount === 1 ? 'it is' : 'they are'} not part
            of this clone. Add {unclonedSkuCount === 1 ? 'it' : 'them'} here
            before saving if you need {unclonedSkuCount === 1 ? 'it' : 'them'}.
          </div>
        )}
        {mode === 'create' && <ProductSkuExplainer />}
        <div className='flex flex-col gap-4 2xl:flex-row 2xl:items-start'>
          <div
            data-product-column
            className='scroll-mt-20 space-y-4 2xl:sticky 2xl:top-20 2xl:max-h-[calc(100vh-6rem)] 2xl:w-[420px] 2xl:shrink-0 2xl:self-start 2xl:overflow-y-auto'
          >
            <div className='flex items-end justify-between font-inter 2xl:min-h-14'>
              <div>
                <h2 className='text-lg font-semibold leading-7 text-gray-900'>
                  Product
                </h2>
                <p className='mt-0.5 text-sm text-gray-500'>
                  What the item is — you add its SKUs alongside.
                </p>
              </div>
              {!lockedProduct &&
                !isProductCollapsed &&
                Boolean(productIdValue) && (
                  <button
                    type='button'
                    onClick={() => setIsProductCollapsed(true)}
                    className='flex shrink-0 items-center gap-1 font-inter text-sm font-medium text-primary hover:underline'
                  >
                    Collapse
                    <ChevronUpIcon className='h-4 w-4' />
                  </button>
                )}
            </div>
            <div
              className={
                isProductCollapsed || lockedProduct
                  ? 'max-2xl:sticky max-2xl:top-16 max-2xl:z-10'
                  : undefined
              }
            >
              <ProductSection
                control={control}
                formState={formState}
                setValue={setValue}
                locked={lockedProduct}
                collapsed={isProductCollapsed && !lockedProduct}
                onToggleCollapsed={() =>
                  setIsProductCollapsed((previous) => !previous)
                }
                onExistingProductSelected={() => setIsProductCollapsed(true)}
                showSkuScopedEditNote={mode === 'edit'}
              />
            </div>
          </div>
          <div className='min-w-0 flex-1 space-y-4'>
            <div>
              <div className='flex items-end justify-between gap-4 font-inter 2xl:min-h-14'>
                <div className='min-w-0 flex-1'>
                  <h2 className='text-lg font-semibold leading-7 text-gray-900'>
                    {mode === 'create' ? 'SKUs for this product' : 'SKU'}
                  </h2>
                  <p className='mt-0.5 text-sm text-gray-500'>
                    {mode === 'create'
                      ? 'Each way you stock it — a single item, a case of 6, a full pallet — is its own SKU with its own pallet stacking.'
                      : 'Details, images and pallet stacking for this SKU.'}
                  </p>
                </div>
                {mode === 'create' && (
                  <span className='shrink-0 whitespace-nowrap text-right text-sm text-gray-500'>
                    {fields.length} SKU{fields.length === 1 ? '' : 's'}
                  </span>
                )}
              </div>
              {mode === 'create' && skuChainSummary && (
                <p
                  className='mt-1 truncate font-inter text-xs text-gray-400'
                  title={skuChainSummary}
                >
                  {skuChainSummary}
                </p>
              )}
            </div>
            {skusArrayError && (
              <p className='text-sm text-red-600'>{skusArrayError}</p>
            )}
            {unmappedServerErrors.length > 0 && (
              <div className='rounded-lg border border-red-200 bg-red-50 p-4'>
                <div className='flex items-center gap-2'>
                  <ExclamationTriangleIcon className='h-5 w-5 shrink-0 text-red-500' />
                  <p className='text-sm font-medium text-red-800'>
                    These need fixing before anything is saved:
                  </p>
                </div>
                <ul className='mt-2 list-disc space-y-1 pl-9 text-sm text-red-700'>
                  {unmappedServerErrors.map((message) => (
                    <li key={message}>{message}</li>
                  ))}
                </ul>
              </div>
            )}
            {fields.map((field, index) => (
              <SkuCard
                key={field.id}
                index={index}
                control={control}
                formState={formState}
                setValue={setValue}
                getValues={getValues}
                mode={mode}
                supplierOptions={supplierOptions}
                onOpenCreateSupplier={() => setSupplierModalCardIndex(index)}
                onDuplicate={() => handleDuplicateSku(index)}
                onRemove={() => remove(index)}
                canRemove={fields.length > 1}
                collapsed={
                  mode === 'edit' ? false : collapsedCardIds.has(field.id)
                }
                onToggleCollapsed={() => toggleCardCollapsed(field.id)}
              />
            ))}
            {mode === 'create' && (
              <button
                type='button'
                onClick={handleAddSku}
                className='flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 p-4 font-inter text-sm font-medium text-gray-600 transition hover:border-gray-400 hover:bg-gray-50'
              >
                <MdAddCircleOutline size={20} className='text-primary' />
                Add SKU
              </button>
            )}
          </div>
        </div>
        <CreateSupplierModal
          isOpen={supplierModalCardIndex !== null}
          onClose={() => setSupplierModalCardIndex(null)}
          onSupplierCreated={handleSupplierCreated}
        />
      </div>
    </FormPage>
  );
}
